import { Modal, Form, Input, Button } from "antd";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createTracking,
  getTrackingByCourse,
  updateTracking,
} from "../../../../api/cours";
import { toast } from "sonner";
import { useEffect, useState } from "react";

const schema = z.object({
  chapter: z.string().min(1, "Le chapitre est requis"),
  description: z.string().min(1, "La description est requise"),
});

type FormData = z.infer<typeof schema>;

type Props = {
  visible: boolean;
  idsCourseVersionEDT: any;
  onCancel: () => void;
  onSave: () => void;
};

const SuivisCourseModal = ({
  visible,
  idsCourseVersionEDT,
  onCancel,
  onSave,
}: Props) => {
  const [currentSuivis, setCurrentSuivis] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      chapter: "",
      description: "",
    },
  });

  const fetchSuivisCours = async () => {
    try {
      const response = await getTrackingByCourse(idsCourseVersionEDT[0]);
      const selectedSuivis = response.data.filter((suivi: any) =>
        idsCourseVersionEDT.includes(suivi?.id_coursVersionEDT),
      );
      setCurrentSuivis(selectedSuivis);
      setValue("chapter", selectedSuivis[0]?.chapter);
      setValue("description", selectedSuivis[0]?.description);
    } catch (error: any) {
      // console.log("erreur de chargement du suivis cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  //   charger le suivis de ce cours
  useEffect(() => {
    if (!visible) return;
    fetchSuivisCours();
  }, [idsCourseVersionEDT, visible]);

  const submit = async (data: FormData) => {
    try {
      if (currentSuivis.length === 0) {
        await Promise.all(
          idsCourseVersionEDT.map((idCourseVersionEDT: any) => {
            createTracking({
              id_coursVersionEDT: idCourseVersionEDT,
              ...data,
            });
          }),
        );
      } else {
        await Promise.all(
          idsCourseVersionEDT.map((idCourseVersionEDT: any) => {
            currentSuivis.map((currentSuivi: any) => {
              updateTracking(currentSuivi.tracking_id, {
                id_coursVersionEDT: idCourseVersionEDT,
                ...data,
              });
            });
          }),
        );
      }

      onSave();
      toast.success("Suivis enregistrer avec succès ! ");
    } catch (error: any) {
      // console.log("erreur de création du suivis cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <Modal
      title="Suivi de cours"
      centered={true}
      open={visible}
      onCancel={() => {
        reset();
        onCancel();
      }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Annuler</Button>
          <Button
            type="primary"
            loading={isSubmitting}
            onClick={handleSubmit(submit)}
          >
            Enregistrer
          </Button>
        </div>
      }
      width={640}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item
          label={
            <>
              Chapitre <span style={{ color: "red" }}>*</span>
            </>
          }
          validateStatus={errors.chapter ? "error" : ""}
          help={errors.chapter?.message}
        >
          <Controller
            name="chapter"
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder="Titre du chapitre" />
            )}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              Description du chapitre <span style={{ color: "red" }}>*</span>
            </>
          }
          validateStatus={errors.description ? "error" : ""}
          help={errors.description?.message}
        >
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder="Décrire le contenu du chapitre"
                autoSize={{ minRows: 6 }}
              />
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SuivisCourseModal;
